import { Drawer, Form, Input, Radio, Select } from "antd";
import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { getCompanies } from "../../../lib/getCompanies";
import { getEmployees } from "../../../lib/getEmployees";
import { registerEmployee } from "../../../lib/registerEmployee";

/**
 * Employee registration drawer.
 * @param {boolean} allowCompanySelect - When true (superadmin), choose company or independent.
 * @param {Function} [onSuccess] - Optional refresh callback after successful register.
 */
const RegisterEmployee = ({
  setEmployeeList,
  openDrawer,
  setOpenDrawer,
  allowCompanySelect = false,
  onSuccess,
}) => {
  const notifyBottomRight = () => {
    toast.success("✔️ Empleado registado exitosamente.", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
    });
  };

  const failNotifyBottomRight = (message) => {
    toast.error(message || "✔️ El DNI ingresado ya está registrado.", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
    });
  };

  const [form] = Form.useForm();
  const [companies, setCompanies] = useState([]);
  const [linkType, setLinkType] = useState("company");

  useEffect(() => {
    if (!allowCompanySelect || !openDrawer) return;
    getCompanies().then((res) => {
      if (res?.status === 200 && Array.isArray(res.data)) {
        setCompanies(res.data);
      }
    });
  }, [allowCompanySelect, openDrawer]);

  useEffect(() => {
    if (openDrawer) {
      setLinkType("company");
      form.setFieldsValue({ linkType: "company" });
    }
  }, [openDrawer, form]);

  const handleFinish = (v) => {
    let employee = { ...v.employee };

    if (allowCompanySelect) {
      if (v.linkType === "independent") {
        employee = {
          ...employee,
          idCompany: 0,
          companyName: "Independiente",
          independent: true,
        };
      } else {
        employee = {
          ...employee,
          idCompany: v.companyId,
        };
      }
    } else {
      const { companyProfile } = JSON.parse(
        window.localStorage.getItem("userDetails")
      );
      employee = { ...employee, idCompany: companyProfile._id };
    }

    registerEmployee(employee).then((res) => {
      if (res.status === 200) {
        if (typeof onSuccess === "function") {
          onSuccess();
        } else if (!allowCompanySelect) {
          const { companyProfile } = JSON.parse(
            window.localStorage.getItem("userDetails")
          );
          getEmployees(companyProfile._id).then((listRes) => {
            setEmployeeList(listRes.data);
          });
        } else if (typeof setEmployeeList === "function") {
          getEmployees().then((listRes) => {
            if (listRes?.status === 200 && Array.isArray(listRes.data)) {
              setEmployeeList(listRes.data);
            }
          });
        }
        form.resetFields();
        setOpenDrawer(false);
        notifyBottomRight();
      } else {
        const msg =
          typeof res?.data === "string"
            ? res.data
            : res?.data?.message || "No se pudo registrar el empleado.";
        failNotifyBottomRight(msg);
      }
    });
  };

  return (
    <>
      <ToastContainer />
      <Drawer
        style={{
          backgroundColor: "#362465",
          borderTopLeftRadius: "15px",
          borderBottomLeftRadius: "15px",
        }}
        destroyOnClose
        width={500}
        closable={false}
        title={<label style={{ color: "white" }}>Nuevo empleado</label>}
        placement={"right"}
        onClose={() => {
          setOpenDrawer(false);
          form.resetFields();
        }}
        open={openDrawer}
        key={"right"}
      >
        <Form
          size="small"
          layout="vertical"
          name="employee"
          form={form}
          initialValues={{ linkType: "company" }}
          onFinish={handleFinish}
        >
          {allowCompanySelect && (
            <>
              <Form.Item
                name="linkType"
                label={<label style={{ color: "white" }}>Vinculación</label>}
                rules={[{ required: true, message: "Seleccioná una opción" }]}
              >
                <Radio.Group
                  onChange={(e) => {
                    setLinkType(e.target.value);
                    if (e.target.value === "independent") {
                      form.setFieldsValue({ companyId: undefined });
                    }
                  }}
                >
                  <Radio value="company" style={{ color: "white" }}>
                    Empresa
                  </Radio>
                  <Radio value="independent" style={{ color: "white" }}>
                    Independiente
                  </Radio>
                </Radio.Group>
              </Form.Item>

              {linkType === "company" && (
                <Form.Item
                  name="companyId"
                  label={<label style={{ color: "white" }}>Empresa</label>}
                  rules={[
                    {
                      required: true,
                      message: "Seleccioná una empresa",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Buscar empresa..."
                    optionFilterProp="label"
                    options={companies.map((company) => ({
                      value: company._id,
                      label: company.cuit
                        ? `${company.name} (${company.cuit})`
                        : company.name,
                    }))}
                    allowClear
                  />
                </Form.Item>
              )}
            </>
          )}

          <Form.Item
            name={["employee", "cuil"]}
            label={<label style={{ color: "white" }}>CUIL</label>}
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name={["employee", "numero_afiliado"]}
            label={<label style={{ color: "white" }}>N° afiliado</label>}
          >
            <Input placeholder="Opcional" />
          </Form.Item>
          <Form.Item
            name={["employee", "name"]}
            label={<label style={{ color: "white" }}>Apellido y Nombre</label>}
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name={["employee", "dni"]}
            label={<label style={{ color: "white" }}>DNI</label>}
            rules={[
              {
                required: true,
                pattern: new RegExp(/^[0-9]+$/),
              },
            ]}
          >
            <Input type="numeric" />
          </Form.Item>

          <Form.Item
            name={["employee", "email"]}
            label={<label style={{ color: "white" }}>Email</label>}
            rules={[
              {
                required: true,
                type: "email",
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name={["employee", "phoneNumber"]}
            label={<label style={{ color: "white" }}>Teléfono</label>}
            rules={[
              {
                required: true,
                pattern: new RegExp(/^[0-9]+$/),
              },
            ]}
          >
            <Input type="numeric" />
          </Form.Item>

          <Form.Item
            name={["employee", "salary"]}
            label={<label style={{ color: "white" }}>Salario bruto</label>}
            rules={[
              {
                required: true,
                pattern: /^\d+(\.\d{1,2})?$/,
              },
            ]}
          >
            <Input type="numeric" />
          </Form.Item>

          <div
            style={{
              justifyContent: "space-between",
              display: "flex",
              marginTop: "25%",
            }}
          >
            <Form.Item>
              <button
                style={{ width: "210px" }}
                type="primary"
                htmlType="submit"
                className="btn btn-primary"
              >
                Guardar
              </button>
            </Form.Item>
            <Form.Item>
              <button
                style={{ width: "210px" }}
                type="primary"
                className="btn btn-danger"
                onClick={(e) => {
                  e.preventDefault();
                  setOpenDrawer(false);
                }}
              >
                Cancelar
              </button>
            </Form.Item>
          </div>
        </Form>
      </Drawer>
    </>
  );
};

export default RegisterEmployee;
